import React, { useEffect, useRef, useState } from 'react';

interface Option {
  label: string;
  value: string;
}

interface EditDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
}

export default function EditDropdown({ value, onChange, options, placeholder = 'Select...' }: EditDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const label = selected ? selected.label : value || placeholder;

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="relative h-10 w-full pl-3.5 pr-11 bg-white rounded-lg border text-left text-[14px] text-ink flex items-center font-normal font-sans"
        style={{
          borderColor: open ? '#0F6E56' : '#D8DEE6',
          boxShadow: open ? '0 0 0 3px rgba(15,110,86,0.14)' : 'none',
        }}
      >
        <span className="truncate flex-1">{label}</span>
        <span className="absolute top-2 bottom-2" style={{ right: 32, width: 1, background: '#D8DEE6' }}></span>
        <svg className="absolute right-3 top-1/2 w-3 h-3 text-ink3" style={{ transform: 'translateY(-50%)' }} viewBox="0 0 12 12" fill="none">
          <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div
          className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-line rounded-lg max-h-80 overflow-y-auto py-1.5"
          style={{ boxShadow: '0 10px 28px rgba(14,26,26,0.12)' }}
        >
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className="w-full text-left pl-10 pr-4 py-2 text-[14px] text-ink hover:bg-panel relative bg-transparent border-none cursor-pointer font-sans"
              >
                {isSelected && (
                  <svg className="absolute left-4 top-1/2 w-3 h-2" style={{ transform: 'translateY(-50%)', color: '#0F6E56' }} viewBox="0 0 12 9" fill="none">
                    <path d="M1 4l3.5 3.5L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}